/**
 * @page /m/profil
 * @description Mobile Portal — Profil, PIN, App-Einstellungen, Abmelden.
 *   Optimiert fuer Smartphone mit vertikalen Sektionen, Edit-Modus fuer
 *   persoenliche Daten, PIN-Aenderung, Dark-Mode + Sprach-Toggle.
 * @features
 *   - Profilbild (Avatar, Initialen-Fallback)
 *   - Persoenliche Daten (editierbar: Vorname, Nachname, Telefon, Notfallkontakt)
 *   - Berufliche Daten (read-only)
 *   - Wochen-SOLL Arbeitszeiten
 *   - PIN aendern (4-Digit, Validierung)
 *   - App-Settings (Dark Mode, Language)
 *   - Abmelden
 *   - Daily Micro-Insight (rotierender Tipp)
 * @api GET /api/portal/profil, PUT /api/portal/profil
 * @api GET /api/portal/ma-dashboard, POST /api/portal/pin-aendern
 * @roles portal_mitarbeiter
 * @i18n mobilePortal.profil
 * @since v2.243.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { ToggleSwitch } from '../../components/ui';
import { istKioskSession } from '../../utils/workSession';
import {
  User, Briefcase, MapPin, Clock, Key, Settings,
  LogOut, Sun, Moon, Globe, Lightbulb, ChevronRight, Shield
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Rotating daily insight based on dashboard data */
function getDailyInsight(dashboard, t) {
  if (!dashboard) return null;
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const tips = [];

  if (dashboard.urlaub_summary?.resturlaub != null) {
    tips.push(t('mobilePortal.profil.insightVacation', `Du hast ${dashboard.urlaub_summary.resturlaub} Urlaubstage uebrig`).replace('{days}', dashboard.urlaub_summary.resturlaub));
  }
  if (dashboard.zeiten_summary?.woche_ist != null) {
    const h = Number(dashboard.zeiten_summary.woche_ist || 0).toFixed(1);
    tips.push(t('mobilePortal.profil.insightWeekHours', `Diese Woche ${h}h gearbeitet`).replace('{hours}', h));
  }
  if (dashboard.zeiten_summary?.wochen_soll != null) {
    const s = Number(dashboard.zeiten_summary.wochen_soll || 0).toFixed(1);
    tips.push(t('mobilePortal.profil.insightWeekSoll', `Dein Wochen-SOLL: ${s}h`).replace('{hours}', s));
  }
  if (dashboard.ungelesene_news > 0) {
    tips.push(t('mobilePortal.profil.insightNews', `${dashboard.ungelesene_news} ungelesene News`).replace('{count}', dashboard.ungelesene_news));
  }

  if (tips.length === 0) return t('mobilePortal.profil.insightDefault', 'Schoenen Arbeitstag!');
  return tips[dayOfYear % tips.length];
}

function InfoField({ label, value }) {
  return (
    <div className="py-2.5 border-b border-[var(--border-card,#E5E7EB)]/50 last:border-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary,#6B7280)] mb-0.5">{label}</p>
      <p className="text-sm text-[var(--text-primary,#1A1A2E)]">{value || '-'}</p>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-6">
      {Icon && <Icon className="w-4 h-4 text-[#338edc]" />}
      <h2 className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary,#4B5563)]">{title}</h2>
    </div>
  );
}

export default function MobileProfil() {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { theme, setTheme, syncToBackend } = useTheme();
  const navigate = useNavigate();

  const [profil, setProfil] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // PIN state
  const [pinData, setPinData] = useState({ alter_pin: '', neuer_pin: '', pin_bestaetigung: '' });
  const [pinMsg, setPinMsg] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);

  // SMS Verification state
  const [verifStatus, setVerifStatus] = useState(null);
  const [verifCode, setVerifCode] = useState('');
  const [verifStep, setVerifStep] = useState('idle'); // idle | sent | checking
  const [verifMsg, setVerifMsg] = useState('');
  const [verifSending, setVerifSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        fetch(`${API}/api/portal/profil`, { credentials: 'include' }),
        fetch(`${API}/api/portal/ma-dashboard`, { credentials: 'include' }),
      ]);
      if (pRes.ok) {
        const p = await pRes.json();
        setProfil(p);
      }
      if (dRes.ok) setDashboard(await dRes.json());
    } catch (err) {
      console.error('MobileProfil load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load verification status
  useEffect(() => {
    fetch(`${API}/api/portal/verifizierung/status`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setVerifStatus(data); })
      .catch(() => {});
  }, []);

  const handleSendVerifCode = async () => {
    setVerifSending(true);
    setVerifMsg('');
    try {
      const res = await fetch(`${API}/api/portal/verifizierung/senden`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setVerifStep('sent');
        setVerifMsg(t('mobilePortal.profil.verifCodeSent', 'Code gesendet an {tel}').replace('{tel}', data.telefon_masked || ''));
      } else {
        setVerifMsg(data.error || t('mobilePortal.profil.verifError', 'Fehler'));
      }
    } catch {
      setVerifMsg(t('mobilePortal.profil.verifError', 'Fehler'));
    } finally {
      setVerifSending(false);
    }
  };

  const handleCheckVerifCode = async () => {
    if (!/^\d{6}$/.test(verifCode)) {
      setVerifMsg(t('mobilePortal.profil.verifCodeFormat', 'Code muss 6 Ziffern haben'));
      return;
    }
    setVerifSending(true);
    setVerifMsg('');
    try {
      const res = await fetch(`${API}/api/portal/verifizierung/pruefen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: verifCode }),
      });
      const data = await res.json();
      if (res.ok && data.verifiziert) {
        setVerifStatus(prev => ({ ...prev, verifiziert: true }));
        setVerifStep('idle');
        setVerifCode('');
        setVerifMsg(t('mobilePortal.profil.verifSuccess', 'Telefonnummer verifiziert!'));
      } else {
        setVerifMsg(data.error || t('mobilePortal.profil.verifWrongCode', 'Falscher Code'));
      }
    } catch {
      setVerifMsg(t('mobilePortal.profil.verifError', 'Fehler'));
    } finally {
      setVerifSending(false);
      setTimeout(() => setVerifMsg(''), 5000);
    }
  };

  const handlePinChange = async () => {
    setPinMsg('');
    if (pinData.neuer_pin !== pinData.pin_bestaetigung) {
      setPinMsg(t('mobilePortal.profil.pinMismatch', 'PINs stimmen nicht ueberein'));
      return;
    }
    if (!/^\d{4}$/.test(pinData.neuer_pin)) {
      setPinMsg(t('mobilePortal.profil.pinFormat', 'PIN muss genau 4 Ziffern sein'));
      return;
    }
    setPinSaving(true);
    try {
      const res = await fetch(`${API}/api/portal/pin-aendern`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ alter_pin: pinData.alter_pin, neuer_pin: pinData.neuer_pin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setPinMsg(t('mobilePortal.profil.pinChanged', 'PIN erfolgreich geaendert'));
        setPinData({ alter_pin: '', neuer_pin: '', pin_bestaetigung: '' });
        setShowPin(false);
      } else {
        setPinMsg(data.error || t('mobilePortal.profil.pinError', 'Fehler'));
      }
    } catch {
      setPinMsg(t('mobilePortal.profil.pinError', 'Fehler'));
    } finally {
      setPinSaving(false);
      setTimeout(() => setPinMsg(''), 4000);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/pin-login');
  };

  const insight = useMemo(() => getDailyInsight(dashboard, t), [dashboard, t]);

  // Weekly SOLL from dashboard (#1543 — Feld heisst soll_woche, nicht wochen_soll)
  const wochenSoll = dashboard?.zeiten_summary?.soll_woche;

  if (loading) {
    return (
      <div className="px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-[var(--bg-inset,#F8F9FC)]" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-[var(--bg-inset,#F8F9FC)] rounded w-2/3" />
              <div className="h-3 bg-[var(--bg-inset,#F8F9FC)] rounded w-1/2" />
            </div>
          </div>
          <div className="h-40 bg-[var(--bg-inset,#F8F9FC)] rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 pb-24">
      {/* Daily Insight */}
      {insight && (
        <div className="bg-[#338edc]/5 border border-[#338edc]/20 rounded-2xl px-4 py-3 mb-4 flex items-center gap-3">
          <Lightbulb className="w-5 h-5 text-[#338edc] flex-shrink-0" />
          <p className="text-xs text-[var(--text-secondary,#4B5563)]">{insight}</p>
        </div>
      )}

      {/* Profile Photo + Name */}
      <div className="flex items-center gap-4 mb-6">
        {profil?.foto_url || profil?.image_url ? (
          <img
            src={profil.foto_url || profil.image_url}
            alt=""
            className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-2 border-[var(--border-card,#E5E7EB)]"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#338edc] to-[#22D3EE] flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-2xl">
              {(profil?.vorname?.[0] || '').toUpperCase()}{(profil?.nachname?.[0] || '').toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-[var(--text-primary,#1A1A2E)] truncate">
            {profil?.vorname} {profil?.nachname}
          </h1>
          {profil?.berufsbezeichnung && (
            <p className="text-sm text-[var(--text-secondary,#4B5563)]">{profil.berufsbezeichnung}</p>
          )}
          {profil?.barcode && (
            <p className="text-xs font-mono text-[var(--text-tertiary,#6B7280)] mt-0.5">{profil.barcode}</p>
          )}
        </div>
      </div>

      {/* 1. Personal Data (editable) */}
      <SectionTitle icon={User} title={t('mobilePortal.profil.personalData', 'Persoenliche Daten')} />
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-2">
        {/* #1547 — read-only: kein Self-Service-Edit der persoenlichen Daten (Aenderung nur durch autorisierte Stellen; kein Save-Endpoint vorhanden) */}
        <InfoField label={t('mobilePortal.profil.firstName', 'Vorname')} value={profil?.vorname} />
        <InfoField label={t('mobilePortal.profil.lastName', 'Nachname')} value={profil?.nachname} />
        <InfoField label={t('mobilePortal.profil.phone', 'Telefon')} value={profil?.mobile_phone || profil?.telefon} />
        <InfoField label={t('mobilePortal.profil.emergencyName', 'Notfallkontakt')} value={profil?.notfallkontakt_name} />
        <InfoField label={t('mobilePortal.profil.emergencyPhone', 'Notfallkontakt Tel.')} value={profil?.notfallkontakt_telefon} />
      </div>

      {/* 2. Work Info (read-only) */}
      <SectionTitle icon={Briefcase} title={t('mobilePortal.profil.workInfo', 'Berufliche Daten')} />
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-2">
        <InfoField label={t('mobilePortal.profil.employeeNr', 'MA-Nr')} value={profil?.barcode} />
        <InfoField label={t('mobilePortal.profil.department', 'Abteilung')} value={profil?.department_name} />
        <InfoField label={t('mobilePortal.profil.location', 'Standort')} value={profil?.standort_name} />
        <InfoField label={t('mobilePortal.profil.startDate', 'Eintritt')} value={formatDate(profil?.vertrag_start || profil?.eintritt_datum)} />
      </div>

      {/* 3. Work Schedule */}
      {wochenSoll != null && (
        <>
          <SectionTitle icon={Clock} title={t('mobilePortal.profil.workSchedule', 'Arbeitszeiten')} />
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary,#4B5563)]">
                {t('mobilePortal.profil.weeklySoll', 'Wochen-SOLL')}
              </span>
              <span className="text-sm font-bold text-[var(--text-primary,#1A1A2E)]">
                {Number(wochenSoll).toFixed(1)}h
              </span>
            </div>
          </div>
        </>
      )}

      {/* 4. PIN aendern */}
      <SectionTitle icon={Key} title={t('mobilePortal.profil.changePin', 'PIN aendern')} />
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-3">
        {!showPin ? (
          <button
            onClick={() => setShowPin(true)}
            className="flex items-center justify-between w-full py-1"
          >
            <span className="text-sm text-[var(--text-secondary,#4B5563)]">
              {t('mobilePortal.profil.changePinBtn', 'Stempel-PIN aendern')}
            </span>
            <ChevronRight className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />
          </button>
        ) : (
          <div className="space-y-2">
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder={t('mobilePortal.profil.oldPin', 'Alter PIN')}
              value={pinData.alter_pin}
              onChange={e => setPinData(prev => ({ ...prev, alter_pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              className="w-full px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] rounded-xl text-sm text-center tracking-[0.5em] text-[var(--text-primary,#1A1A2E)] placeholder:tracking-normal placeholder:text-[var(--text-tertiary,#6B7280)] focus:outline-none focus:border-[#338edc]"
            />
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder={t('mobilePortal.profil.newPin', 'Neuer PIN')}
              value={pinData.neuer_pin}
              onChange={e => setPinData(prev => ({ ...prev, neuer_pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              className="w-full px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] rounded-xl text-sm text-center tracking-[0.5em] text-[var(--text-primary,#1A1A2E)] placeholder:tracking-normal placeholder:text-[var(--text-tertiary,#6B7280)] focus:outline-none focus:border-[#338edc]"
            />
            <input
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder={t('mobilePortal.profil.confirmPin', 'PIN bestaetigen')}
              value={pinData.pin_bestaetigung}
              onChange={e => setPinData(prev => ({ ...prev, pin_bestaetigung: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              className="w-full px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] rounded-xl text-sm text-center tracking-[0.5em] text-[var(--text-primary,#1A1A2E)] placeholder:tracking-normal placeholder:text-[var(--text-tertiary,#6B7280)] focus:outline-none focus:border-[#338edc]"
            />
            <div className="flex gap-2 pt-1">
              <button
                onClick={handlePinChange}
                disabled={pinSaving || !pinData.alter_pin || !pinData.neuer_pin || !pinData.pin_bestaetigung}
                className="flex-1 px-4 py-2.5 bg-[#338edc] text-white rounded-xl text-sm font-semibold active:bg-[#2a7bc5] disabled:opacity-50 transition-colors"
              >
                {pinSaving ? '...' : t('mobilePortal.profil.savePin', 'PIN speichern')}
              </button>
              <button
                onClick={() => { setShowPin(false); setPinData({ alter_pin: '', neuer_pin: '', pin_bestaetigung: '' }); setPinMsg(''); }}
                className="px-4 py-2.5 bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] rounded-xl text-sm font-semibold transition-colors"
              >
                {t('mobilePortal.profil.cancel', 'Abbrechen')}
              </button>
            </div>
          </div>
        )}
        {pinMsg && (
          <p className={`text-xs mt-2 ${pinMsg.includes('erfolgreich') || pinMsg.includes('success') ? 'text-green-600' : 'text-red-500'}`}>
            {pinMsg}
          </p>
        )}
      </div>

      {/* 4b. Telefon-Verifizierung */}
      {verifStatus && (
        <>
          <SectionTitle icon={Shield} title={t('mobilePortal.profil.phoneVerification', 'Telefon-Verifizierung')} />
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-3">
            {verifStatus.verifiziert ? (
              <div className="flex items-center gap-3 py-1">
                <span className="text-green-500 text-lg">&#10003;</span>
                <div>
                  <p className="text-sm font-medium text-[var(--text-primary,#1A1A2E)]">
                    {t('mobilePortal.profil.phoneVerified', 'Nummer verifiziert')}
                  </p>
                  <p className="text-xs text-[var(--text-tertiary,#6B7280)]">
                    {verifStatus.telefon_masked}
                  </p>
                </div>
              </div>
            ) : !verifStatus.hat_telefon ? (
              <p className="text-sm text-[var(--text-tertiary,#6B7280)] py-1">
                {t('mobilePortal.profil.noPhone', 'Keine Telefonnummer hinterlegt. Bitte unter "Persönliche Daten" ergänzen.')}
              </p>
            ) : verifStep === 'idle' ? (
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-secondary,#4B5563)]">
                  {t('mobilePortal.profil.verifDesc', 'Verifiziere deine Nummer für PIN-Reset per SMS.')}
                </p>
                <button
                  onClick={handleSendVerifCode}
                  disabled={verifSending}
                  className="w-full px-4 py-2.5 bg-[#338edc] text-white rounded-xl text-sm font-semibold active:bg-[#2a7bc5] disabled:opacity-50 transition-colors"
                >
                  {verifSending ? '...' : t('mobilePortal.profil.sendCode', 'Code per SMS senden')}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-[var(--text-secondary,#4B5563)]">
                  {t('mobilePortal.profil.enterCode', '6-stelligen Code eingeben:')}
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifCode}
                  onChange={e => setVerifCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] rounded-xl text-sm text-center tracking-[0.5em] text-[var(--text-primary,#1A1A2E)] placeholder:tracking-normal placeholder:text-[var(--text-tertiary,#6B7280)] focus:outline-none focus:border-[#338edc]"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCheckVerifCode}
                    disabled={verifSending || verifCode.length < 6}
                    className="flex-1 px-4 py-2.5 bg-[#338edc] text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors"
                  >
                    {verifSending ? '...' : t('mobilePortal.profil.verify', 'Verifizieren')}
                  </button>
                  <button
                    onClick={() => { setVerifStep('idle'); setVerifCode(''); setVerifMsg(''); }}
                    className="px-4 py-2.5 bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] rounded-xl text-sm font-semibold transition-colors"
                  >
                    {t('mobilePortal.profil.cancel', 'Abbrechen')}
                  </button>
                </div>
              </div>
            )}
            {verifMsg && (
              <p className={`text-xs mt-2 ${verifMsg.includes('verifiziert') || verifMsg.includes('gesendet') || verifMsg.includes('verified') || verifMsg.includes('sent') ? 'text-green-600' : 'text-red-500'}`}>
                {verifMsg}
              </p>
            )}
          </div>
        </>
      )}

      {/* 5. App Settings — am registrierten Terminal gilt hart hell und Deutsch
          (#1809-Fix). Beide Schalter waeren dort tote Bedienung, die das System
          sofort zurueckdreht; die Karte entfaellt deshalb ganz statt leer zu
          bleiben. Auf jedem anderen Geraet, auch dem unregistrierten, sind sie da. */}
      {!istKioskSession(user) && (
      <>
      <SectionTitle icon={Settings} title={t('mobilePortal.profil.appSettings', 'Einstellungen')} />
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-2xl px-4 py-2">
        {/* Dark Mode */}
        <div className="flex items-center justify-between py-3 border-b border-[var(--border-card,#E5E7EB)]/50">
          <div className="flex items-center gap-3">
            {theme === 'dark' ? <Moon className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" /> : <Sun className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />}
            <span className="text-sm text-[var(--text-primary,#1A1A2E)]">{t('mobilePortal.profil.darkMode', 'Dark Mode')}</span>
          </div>
          <ToggleSwitch
            checked={theme === 'dark'}
            onChange={() => {
              const next = theme === 'dark' ? 'light' : 'dark';
              setTheme(next);
              syncToBackend(next);
            }}
          />
        </div>

        {/* Language Toggle */}
        <div className="flex items-center justify-between py-3">
          <div className="flex items-center gap-3">
            <Globe className="w-4 h-4 text-[var(--text-tertiary,#6B7280)]" />
            <span className="text-sm text-[var(--text-primary,#1A1A2E)]">{t('mobilePortal.profil.language', 'Sprache')}</span>
          </div>
          <div className="flex bg-[var(--bg-inset,#F8F9FC)] rounded-lg p-0.5 border border-[var(--border-card,#E5E7EB)]">
            <button
              onClick={() => setLanguage('de')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                language === 'de' ? 'bg-[var(--bg-card,#fff)] shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'
              }`}
            >
              DE
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${
                language === 'en' ? 'bg-[var(--bg-card,#fff)] shadow-sm text-[var(--text-primary,#1A1A2E)]' : 'text-[var(--text-tertiary,#6B7280)]'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>
      </>
      )}

      {/* 6. Abmelden */}
      <div className="mt-8">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl text-red-600 dark:text-red-400 text-sm font-semibold active:bg-red-100 dark:active:bg-red-900/30 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t('mobilePortal.profil.logout', 'Abmelden')}
        </button>
      </div>

      {/* Version */}
      <p className="text-center text-[10px] text-[var(--text-tertiary,#6B7280)] mt-4">
        WeShip Portal v2
      </p>
    </div>
  );
}
